<template>
  <div class="w-100">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="text-muted mt-2">Cargando información del usuario...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
      <button class="btn btn-sm btn-danger ms-3" @click="getProfile">
        <font-awesome-icon icon="sync" class="me-1" />
        Reintentar
      </button>
    </div>

    <div v-else-if="user" class="card mb-4">
      <div class="card-header pb-0">
        <div class="row align-items-center">
          <div class="col">
            <h6 class="mb-0">
              <font-awesome-icon icon="user" class="me-2" />
              Detalles del Usuario
            </h6>
            <p class="text-sm text-muted mb-0 mt-1">ID: {{ user._id }}</p>
          </div>
          <div class="col-auto">
            <span 
              class="badge"
              :class="user.status ? 'bg-success' : 'bg-secondary'"
            >
              {{ user.status ? 'ACTIVO' : 'INACTIVO' }}
            </span>
          </div>
        </div>
      </div>
      <div class="card-body px-4">
        <div class="row">
          <!-- Información del Usuario -->
          <div class="col-lg-4 col-md-12 mb-4">
            <h6 class="text-uppercase text-muted text-xs font-weight-bolder mb-3">Información del Usuario</h6>
            <ul v-if="user" class="list-unstyled">
              <li class="mb-3">
                <span class="text-muted">Nombre:</span><br>
                <strong class="font-weight-bold">{{ user.name }}</strong>
              </li>
              <li class="mb-3">
                <span class="text-muted">Email:</span><br>
                <strong>{{ user.email }}</strong>
              </li>
              <li class="mb-3" v-if="user.type">
                <span class="text-muted">Rol:</span><br>
                <span class="badge bg-secondary text-capitalize">{{ user.type }}</span>
              </li>
              <li class="mb-3">
                <span class="text-muted">Fecha de Registro:</span><br>
                <strong>{{ $filters.formatDate(user.created_at) }}</strong>
              </li>
            </ul>
          </div>

          <!-- Editar Información -->
          <div class="col-lg-4 col-md-12 mb-4">
            <h6 class="text-uppercase text-muted text-xs font-weight-bolder mb-3">Editar Información</h6>
            <form @submit.prevent="updateProfile">
              <div class="mb-3">
                <label class="form-label">Nombre</label>
                <input type="text" class="form-control" v-model="form.name" required>
              </div>
              <div class="mb-3">
                <label class="form-label">Email</label>
                <input type="email" class="form-control" v-model="form.email" required>
              </div>
              <div class="mb-3">
                <label class="form-label">Rol</label>
                <select class="form-select" v-model="form.type" required>
                  <option value="">Seleccione un rol</option>
                  <option value="agent">Agent</option>
                  <option value="administrator">Administrador del Sistema</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Estado</label>
                <select class="form-select" v-model="form.status">
                  <option value="true">ACTIVO</option>
                  <option value="false">INACTIVO</option>
                </select>
              </div>
              <button type="submit" class="btn bg-gradient-success w-100">
                <font-awesome-icon icon="save" class="me-1" />
                Actualizar Información
              </button>
            </form>
          </div>

          <!-- Cambiar Contraseña -->
          <div class="col-lg-4 col-md-12 mb-4" v-if="show_avatar">
            <h6 class="text-uppercase text-muted text-xs font-weight-bolder mb-3">Cambiar Contraseña</h6>
            <div class="alert alert-warning text-sm" role="alert">
              <font-awesome-icon icon="exclamation-triangle" class="me-1" />
              <strong>Importante:</strong> El usuario necesitará iniciar sesión nuevamente
            </div>
            <div class="mb-3">
              <label class="form-label">Nueva Contraseña</label>
              <input type="password" class="form-control" v-model="new_password" placeholder="Ingrese nueva contraseña">
            </div>
            <button class="btn bg-gradient-primary w-100" @click="updatePassword">
              <font-awesome-icon icon="key" class="me-1" />
              Actualizar Contraseña
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, post } from "@/utils/httpRequest";
import { library } from '@fortawesome/fontawesome-svg-core';
import { faUser, faSave, faKey, faExclamationTriangle, faSync, faEye } from '@fortawesome/free-solid-svg-icons';

library.add(faUser, faSave, faKey, faExclamationTriangle, faSync, faEye);

export default {
  name: "AgentDetail",
  data: () => ({
    user_id: null,
    user: null,
    loading: false,
    error: null,
    tabSelected: 'transactions',
    url_avatar: '',
    show_avatar: true,
    form: {
      name: '',
      email: '',
      percentage: 0,
      password: '',
      type: '',
      phone: '',
      status: true
    },
    new_password: ''
  }),
  mounted() {
    this.user_id = this.$route.query.id;
    if (!this.user_id) {
      this.error = 'No se ha especificado un ID de usuario';
      return;
    }
    this.getProfile();
  },
  methods: {
    async getProfile() {
      this.loading = true;
      this.error = null;
      
      try {
        const { data } = await get(`users/find-one?id=${this.user_id}`, null);
        this.user = data;
        // Preencher el formulario con los datos actuales
        this.form = {
          name: data.name || '',
          email: data.email || '',
          percentage: data.percentage || 0,
          password: '',
          type: data.type || '',
          phone: data.phone || '',
          status: data.status !== undefined ? data.status.toString() : 'true'
        };
      } catch (err) {
        console.error('Error loading user profile:', err);
        this.error = 'No se pudo cargar la información del usuario. Por favor, verifica que el ID sea correcto.';
      } finally {
        this.loading = false;
      }
    },

    async selectImage(e) {
      this.url_avatar = e.target.files[0];
    },

    async updateProfile() {
      // Validación básica
      if (!this.form.name || !this.form.email || !this.form.type) {
        this.$swal({
          title: 'Campos incompletos',
          text: 'Por favor, completa todos los campos requeridos.',
          icon: 'warning',
          confirmButtonColor: '#ea0606'
        });
        return;
      }

      try {
        const result = await this.$swal({
          title: '¿Actualizar usuario?',
          html: `¿Estás seguro de actualizar la información de <strong>${this.form.name}</strong>?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sí, actualizar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#82d616',
          cancelButtonColor: '#6c757d',
          reverseButtons: true
        });

        if (result.isConfirmed) {
          const payload = {
            name: this.form.name,
            email: this.form.email,
            type: this.form.type,
            status: this.form.status === 'true',
            _id: this.user_id
          };
          
          await post('users/update', payload);
          await this.getProfile();
          
          await this.$swal({
            title: '¡Actualizado!',
            html: `La información de <strong>${this.user.name}</strong> ha sido actualizada exitosamente.`,
            icon: 'success',
            timer: 2500,
            showCancelButton: false,
            showConfirmButton: false,
          });
        }
      } catch (err) {
        console.error('Error updating profile:', err);
        this.$swal({
          title: 'Error',
          text: 'No se pudo actualizar la información del usuario. Por favor, intenta nuevamente.',
          icon: 'error',
          confirmButtonColor: '#ea0606'
        });
      }
    },

    async updatePassword() {
      if (!this.new_password || this.new_password.length < 6) {
        this.$swal({
          title: 'Contraseña inválida',
          text: 'La contraseña debe tener al menos 6 caracteres.',
          icon: 'warning',
          confirmButtonColor: '#ea0606'
        });
        return;
      }

      try {
        await post('users/update-password', { password: this.new_password, _id: this.user_id });
        this.new_password = '';
        
        await this.$swal({
          title: '¡Contraseña actualizada!',
          html: 'La contraseña ha sido actualizada exitosamente.<br>El usuario necesitará iniciar sesión nuevamente.',
          icon: 'success',
          timer: 2500,
          showCancelButton: false,
          showConfirmButton: false,
        });
      } catch (err) {
        console.error('Error updating password:', err);
        this.$swal({
          title: 'Error',
          text: 'No se pudo actualizar la contraseña. Por favor, intenta nuevamente.',
          icon: 'error',
          confirmButtonColor: '#ea0606'
        });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.card-header h6 {
  font-weight: 600;
  color: #344767;
}

.text-muted {
  font-size: 0.875rem;
}

.list-unstyled li {
  padding: 0.5rem 0;
  border-bottom: 1px solid #f0f2f5;
  
  &:last-child {
    border-bottom: none;
  }
}

.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 0.5rem;
}

.alert {
  font-size: 0.875rem;
}

.btn {
  font-weight: 600;
  
  svg {
    margin-right: 0.5rem;
  }
}

:deep(.swal2-popup) {
  font-family: inherit;
}
</style>
